import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Heart, MessageCircle, Send, Pencil, Users, TrendingUp, TrendingDown, AlertCircle, Clock, Film, Images, Image as ImageIcon, ExternalLink, Instagram, Megaphone } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { toast } from 'react-toastify';
import { competencia, errorMsg } from './competencia.api';

/* Marketing › Tu competencia: el cliente carga hasta 3 cuentas de Instagram de su rubro y las compara con la suya
   (seguidores, interacción por post, ritmo de publicación y últimos posts). Los datos se actualizan una vez por día. */

const COLORES = ['#2563eb', '#f97316', '#8b5cf6', '#10b981'];
const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('es-AR'));
const corto = (n) => (n >= 10000 ? `${(n / 1000).toLocaleString('es-AR', { maximumFractionDigits: 1 })} mil` : fmt(n));
const fechaCorta = (iso) => new Date(String(iso).length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
const ICONO_TIPO = { reel: Film, video: Film, carrusel: Images, imagen: ImageIcon };

function Avatar({ c, color, size = 44 }) {
  const [mal, setMal] = useState(false);
  const letra = (c.nombre || c.username || '?').charAt(0).toUpperCase();
  return c.foto && !mal
    ? <img src={c.foto} alt="" referrerPolicy="no-referrer" onError={() => setMal(true)} className="rounded-full object-cover shrink-0" style={{ width: size, height: size, boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${color}` }} />
    : <div className="rounded-full shrink-0 flex items-center justify-center text-white font-bold" style={{ width: size, height: size, background: color }}>{letra}</div>;
}

// Biblioteca de anuncios de Meta (pública): la API no da avisos comerciales fuera de Europa, así que se abre la búsqueda.
const anunciosDe = (c) => `https://www.facebook.com/ads/library/?active_status=active&ad_type=all&country=AR&media_type=all&search_type=keyword_unordered&q=${encodeURIComponent(c.nombre || c.username)}`;

function variacion(hist) {
  if (!hist || hist.length < 2) return null;
  const ult = hist[hist.length - 1];
  const limite = new Date(Date.parse(ult.fecha) - 7 * 86400_000).toISOString().slice(0, 10);
  const base = [...hist].reverse().find((p) => p.fecha <= limite) || hist[0];
  if (base.fecha === ult.fecha) return null;
  const dias = Math.round((Date.parse(ult.fecha) - Date.parse(base.fecha)) / 86400_000);
  return { delta: ult.seguidores - base.seguidores, dias };
}

function Tarjeta({ c, color, hist, lider }) {
  const m = c.metricas || {};
  const v = variacion(hist);
  const esPropia = c.rol === 'propia';
  return (
    <div className={`bg-white border rounded-2xl p-4 flex flex-col gap-3 min-w-0 ${esPropia ? 'border-blue-300 ring-1 ring-blue-100' : 'border-gray-200'}`}>
      <div className="flex items-center gap-3 min-w-0">
        <Avatar c={c} color={color} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <a href={`https://instagram.com/${c.username}`} target="_blank" rel="noreferrer" className="font-semibold text-gray-900 truncate hover:underline">@{c.username}</a>
            {esPropia && <span className="shrink-0 text-[10px] font-bold bg-blue-600 text-white px-1.5 py-0.5 rounded-full">VOS</span>}
          </div>
          <div className="text-xs text-gray-500 truncate">{c.estado === 'ok' ? c.nombre : ' '}</div>
        </div>
      </div>

      {c.estado === 'ok' ? (
        <>
          <div>
            <div className="text-[11px] text-gray-500">Seguidores</div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-2xl font-bold text-gray-900">{fmt(c.seguidores)}</span>
              {v && (
                <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${v.delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {v.delta >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}{v.delta >= 0 ? '+' : ''}{fmt(v.delta)} en {v.dias} {v.dias === 1 ? 'día' : 'días'}
                </span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className={`rounded-xl py-2 px-1 ${lider ? 'bg-amber-50' : 'bg-gray-50'}`}>
              <div className="text-sm font-bold text-gray-900">{fmt(m.interaccionProm)}</div>
              <div className="text-[10px] leading-tight text-gray-500">interacción<br />por post</div>
            </div>
            <div className="rounded-xl bg-gray-50 py-2 px-1">
              <div className="text-sm font-bold text-gray-900">{m.tasa != null ? `${m.tasa.toLocaleString('es-AR')}%` : '—'}</div>
              <div className="text-[10px] leading-tight text-gray-500">sobre sus<br />seguidores</div>
            </div>
            <div className="rounded-xl bg-gray-50 py-2 px-1">
              <div className="text-sm font-bold text-gray-900">{m.postsSemana != null ? m.postsSemana.toLocaleString('es-AR') : '—'}</div>
              <div className="text-[10px] leading-tight text-gray-500">posts<br />por semana</div>
            </div>
          </div>
          {lider && <div className="text-[11px] font-semibold text-amber-700">La que más interacción genera</div>}
          {!esPropia && (
            <a href={anunciosDe(c)} target="_blank" rel="noreferrer" className="mt-auto inline-flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50">
              <Megaphone size={14} />Buscar sus anuncios
            </a>
          )}
        </>
      ) : c.estado === 'pendiente' ? (
        <div className="flex items-start gap-2 text-sm text-gray-500 bg-gray-50 rounded-xl p-3"><Clock size={16} className="shrink-0 mt-0.5" />Los datos aparecen en las próximas horas.</div>
      ) : (
        <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 rounded-xl p-3"><AlertCircle size={16} className="shrink-0 mt-0.5" />{c.mensaje || 'No pudimos leer esta cuenta.'}</div>
      )}
    </div>
  );
}

function Editor({ inicial, max, onGuardar, onCancelar, guardando }) {
  const [propia, setPropia] = useState(inicial.propia || '');
  const [comp, setComp] = useState(() => Array.from({ length: max }, (_, i) => inicial.competidores?.[i] || ''));
  const campo = (valor, set, placeholder, key) => (
    <label key={key} className="flex items-center border border-gray-300 rounded-xl bg-white focus-within:ring-2 focus-within:ring-blue-200 focus-within:border-blue-400">
      <span className="pl-3 text-gray-400">@</span>
      <input value={valor} onChange={(e) => set(e.target.value)} placeholder={placeholder} autoCapitalize="none" autoCorrect="off" spellCheck={false}
        className="flex-1 min-w-0 px-2 py-2.5 text-sm bg-transparent outline-none" />
    </label>
  );
  return (
    <form onSubmit={(e) => { e.preventDefault(); onGuardar(propia, comp.filter((x) => x.trim())); }} className="bg-white border border-gray-200 rounded-2xl p-4 md:p-5 space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-sm font-semibold text-gray-700 mb-1.5">Tu Instagram</div>
          {campo(propia, setPropia, 'tuconstructora', 'p')}
        </div>
        <div>
          <div className="text-sm font-semibold text-gray-700 mb-1.5">Tus competidores (hasta {max})</div>
          <div className="space-y-2">
            {comp.map((v, i) => campo(v, (x) => setComp((a) => a.map((y, j) => (j === i ? x : y))), `competidor${i + 1}`, i))}
          </div>
        </div>
      </div>
      <p className="text-xs text-gray-500">Tienen que ser cuentas públicas de empresa o creador. Podés pegar el usuario o el link del perfil.</p>
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
        {onCancelar && <button type="button" onClick={onCancelar} className="rounded-xl px-5 py-2.5 text-sm font-semibold border border-gray-300 text-gray-800 bg-white hover:bg-gray-50">Cancelar</button>}
        <button type="submit" disabled={guardando} className="rounded-xl px-5 py-2.5 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60">{guardando ? 'Buscando cuentas…' : 'Guardar'}</button>
      </div>
    </form>
  );
}

function Evolucion({ cuentas, historial, colorDe }) {
  const ok = cuentas.filter((c) => c.estado === 'ok');
  const filas = useMemo(() => {
    const porFecha = {};
    // Claves sin puntos: recharts lee "a.b" como ruta anidada.
    ok.forEach((c, i) => (historial[c.username] || []).forEach((p) => { if (!porFecha[p.fecha]) porFecha[p.fecha] = { fecha: p.fecha }; porFecha[p.fecha][`c${i}`] = p.seguidores; }));
    return Object.values(porFecha).sort((a, b) => a.fecha.localeCompare(b.fecha));
  }, [ok, historial]);
  if (!ok.length) return null;
  const maxSeg = Math.max(...ok.map((c) => c.seguidores || 0), 1);
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 md:p-5">
      <div className="text-sm font-semibold text-gray-700">Evolución de seguidores</div>
      {filas.length >= 2 ? (
        <div className="h-64 mt-3 -ml-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={filas} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="fecha" tickFormatter={fechaCorta} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} minTickGap={24} />
              <YAxis tickFormatter={corto} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} width={52} domain={['auto', 'auto']} />
              <Tooltip labelFormatter={fechaCorta} formatter={(v, n) => [fmt(v), `@${n}`]} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
              {ok.map((c, i) => <Line key={c.username} dataKey={`c${i}`} name={c.username} stroke={colorDe(c.username)} strokeWidth={c.rol === 'propia' ? 3 : 2} dot={false} connectNulls />)}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-500 mt-0.5 mb-3">La curva se arma día a día desde que agregaste las cuentas. Por ahora, la foto de hoy:</p>
          <div className="space-y-2.5">
            {[...ok].sort((a, b) => b.seguidores - a.seguidores).map((c) => (
              <div key={c.username} className="flex items-center gap-2 text-sm">
                <div className="w-24 sm:w-36 truncate text-gray-700 shrink-0">@{c.username}</div>
                <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.max(3, (c.seguidores / maxSeg) * 100)}%`, background: colorDe(c.username) }} /></div>
                <div className="w-16 text-right font-semibold text-gray-900 shrink-0">{corto(c.seguidores)}</div>
              </div>
            ))}
          </div>
        </>
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
        {ok.map((c) => <span key={c.username} className="inline-flex items-center gap-1.5 text-xs text-gray-600"><span className="w-2.5 h-2.5 rounded-full" style={{ background: colorDe(c.username) }} />@{c.username}</span>)}
      </div>
    </div>
  );
}

function Post({ p }) {
  const [mal, setMal] = useState(false);
  const Tipo = ICONO_TIPO[p.tipo] || ImageIcon;
  return (
    <a href={p.link} target="_blank" rel="noreferrer" className="group bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col min-w-0 hover:shadow-md transition-shadow">
      <div className="relative bg-gray-100 overflow-hidden" style={{ aspectRatio: "1 / 1" }}>
        {p.imagen && !mal
          ? <img src={p.imagen} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setMal(true)} className="absolute inset-0 w-full h-full object-cover" />
          : p.video
            ? <video src={`${p.video}#t=0.5`} muted playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover" />
            : <div className="absolute inset-0 flex items-center justify-center text-gray-300"><Instagram size={28} /></div>}
        <span className="absolute top-1.5 right-1.5 bg-black/55 text-white rounded-md p-1"><Tipo size={12} /></span>
      </div>
      <div className="p-2 space-y-1">
        <div className="flex items-center gap-2.5 text-xs text-gray-800 font-semibold flex-wrap">
          <span className="inline-flex items-center gap-1"><Heart size={13} className="text-rose-500" />{p.likes == null ? <span className="font-normal text-gray-400">oculto</span> : corto(p.likes)}</span>
          <span className="inline-flex items-center gap-1"><MessageCircle size={13} className="text-sky-500" />{corto(p.comentarios)}</span>
          {p.compartidos != null && <span className="inline-flex items-center gap-1"><Send size={13} className="text-emerald-500" />{corto(p.compartidos)}</span>}
        </div>
        <div className="text-[11px] text-gray-500 line-clamp-2 min-h-[2.1em]">{p.texto || ' '}</div>
        <div className="text-[10px] text-gray-400 flex items-center justify-between">{fechaCorta(p.fecha)}<ExternalLink size={10} className="opacity-0 group-hover:opacity-100" /></div>
      </div>
    </a>
  );
}

function UltimosPosts({ cuentas, colorDe }) {
  const ok = cuentas.filter((c) => c.estado === 'ok' && c.posts?.length);
  const [sel, setSel] = useState(null);
  const actual = ok.find((c) => c.username === sel) || ok[0];
  if (!actual) return null;
  const posts = [...actual.posts].slice(0, 12);
  const mejor = posts.reduce((a, p) => ((p.likes || 0) + p.comentarios > (a ? (a.likes || 0) + a.comentarios : -1) ? p : a), null);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-semibold text-gray-700">Últimos posts</div>
        <div className="flex gap-1.5 overflow-x-auto max-w-full pb-1 -mb-1">
          {ok.map((c) => (
            <button key={c.username} type="button" onClick={() => setSel(c.username)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${c.username === actual.username ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300'}`}>
              <span className="w-2 h-2 rounded-full" style={{ background: colorDe(c.username) }} />@{c.username}
            </button>
          ))}
        </div>
      </div>
      {mejor && (
        <p className="text-xs text-gray-500">
          El que mejor le fue a @{actual.username}: <span className="font-semibold text-gray-700">{fmt((mejor.likes || 0) + mejor.comentarios)} interacciones</span> el {fechaCorta(mejor.fecha)}.
          {actual.rol !== 'propia' && ' Los compartidos de otras cuentas no son públicos.'}
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 sm:gap-3">
        {posts.map((p) => <Post key={p.id} p={p} />)}
      </div>
    </div>
  );
}

export default function TuCompetencia({ user, isAdmin }) {
  const customerId = user?.customerId;
  const [datos, setDatos] = useState(null);
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(() => {
    if (!customerId) return;
    competencia.ver(customerId).then(setDatos).catch((e) => { toast.error(errorMsg(e)); setDatos({ error: true, cuentas: [], historial: {}, config: {}, max: 3 }); });
  }, [customerId]);
  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async (propia, competidores) => {
    setGuardando(true);
    try {
      const r = await competencia.guardar(customerId, propia, competidores);
      setDatos(r);
      setEditando(false);
      if (r.aviso) toast.info(r.aviso);
      else toast.success('Listo, ya estás siguiendo a tu competencia.');
    } catch (e) {
      toast.error(errorMsg(e));
    } finally {
      setGuardando(false);
    }
  };

  const cuentas = datos?.cuentas || [];
  const colorDe = useMemo(() => {
    const mapa = {};
    let i = 1;
    cuentas.forEach((c) => { mapa[c.username] = c.rol === 'propia' ? COLORES[0] : COLORES[i++ % COLORES.length]; });
    return (u) => mapa[u] || '#9ca3af';
  }, [cuentas]);
  const lider = useMemo(() => {
    const ok = cuentas.filter((c) => c.estado === 'ok');
    if (ok.length < 2) return null;
    return ok.reduce((a, c) => ((c.metricas?.interaccionProm || 0) > (a.metricas?.interaccionProm || 0) ? c : a)).username;
  }, [cuentas]);

  if (!datos) return <div className="space-y-4"><div className="h-24 bg-white border border-gray-200 rounded-2xl animate-pulse" /><div className="h-48 bg-white border border-gray-200 rounded-2xl animate-pulse" /></div>;

  const vacio = !cuentas.length;
  const competidores = cuentas.filter((c) => c.rol === 'competidor');

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-2xl p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800">Tu competencia</h2>
          <p className="text-sm text-gray-500 mt-0.5">Mirá qué publican hasta {datos.max} cuentas de tu rubro, cuántos seguidores tienen y cuánta interacción logran, al lado de la tuya. Se actualiza todos los días.</p>
        </div>
        {!vacio && !editando && (
          <button type="button" onClick={() => setEditando(true)} className="self-start md:self-auto inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold border border-gray-300 text-gray-800 bg-white hover:bg-gray-50"><Pencil size={15} />Cambiar cuentas</button>
        )}
      </div>

      {!datos.habilitado && !vacio && (
        <div className="flex items-start gap-2 text-sm bg-blue-50 text-blue-900 border border-blue-100 rounded-2xl p-3">
          <Clock size={16} className="shrink-0 mt-0.5" />
          <span>Ya guardamos tus cuentas. Los números aparecen en las próximas horas.{isAdmin && ' (Admin: falta COMPETENCIA_IG_TOKEN en el backend.)'}</span>
        </div>
      )}

      {(vacio || editando) && (
        <>
          {vacio && (
            <div className="flex items-start gap-3 bg-gradient-to-r from-blue-50 to-orange-50 border border-gray-200 rounded-2xl p-4">
              <Users size={20} className="text-blue-600 shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">Cargá tu Instagram y el de hasta {datos.max} constructoras o desarrolladoras con las que competís. Te mostramos quién crece más y qué posteos les funcionan.</p>
            </div>
          )}
          <Editor inicial={datos.config || {}} max={datos.max} guardando={guardando} onGuardar={guardar} onCancelar={vacio ? null : () => setEditando(false)} />
        </>
      )}

      {!vacio && (
        <>
          <div className={`grid gap-3 grid-cols-1 sm:grid-cols-2 ${cuentas.length >= 4 ? 'xl:grid-cols-4' : cuentas.length === 3 ? 'lg:grid-cols-3' : ''}`}>
            {cuentas.map((c) => <Tarjeta key={c.username} c={c} color={colorDe(c.username)} hist={datos.historial?.[c.username]} lider={c.username === lider} />)}
          </div>
          {!competidores.length && !editando && (
            <button type="button" onClick={() => setEditando(true)} className="w-full border-2 border-dashed border-gray-300 rounded-2xl p-4 text-sm font-semibold text-gray-600 hover:bg-white">+ Agregá hasta {datos.max} competidores</button>
          )}
          <Evolucion cuentas={cuentas} historial={datos.historial || {}} colorDe={colorDe} />
          <UltimosPosts cuentas={cuentas} colorDe={colorDe} />
        </>
      )}
    </div>
  );
}
